/**
 * Client-side binding for the getRisques Cloud Function + cache.
 *
 * Historiquement : pas de cache, refetch systématique à chaque ouverture
 * de fiche. Rationale initiale : "Géorisques update fréquemment, on
 * veut toujours fresh". Problème en pratique : quand la CF 503 (cold
 * start, rate limit upstream, hiccup réseau), ouvrir une fiche plante
 * la section risques à CHAQUE fois, sans possibilité de retry avec
 * backoff gracieux.
 *
 * Nouveau : cache par fiche avec même protocole que DVF.
 *   - Firestore `/users/{uid}/fiches/{fid}/risques/latest`
 *   - TTL 30 jours (Géorisques release mensuel typique : nouveaux PPRI,
 *     nouveaux SEVESO, mise à jour catnat)
 *   - inputsHash = lat/lng clampés à 4 décimales (≈ 11 m)
 *   - Résilience 503 : si la CF plante ET qu'on a un cache stale
 *     disponible, on sert le stale plutôt que de throw. Un rapport
 *     Géorisques vieux d'un mois vaut mieux que "non disponible".
 */

import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { auth, db, functions } from "./firebase";
import type { RisquesReport } from "./types";

const callGetRisques = httpsCallable<
  { lat: number; lng: number },
  RisquesReport
>(functions, "getRisques");

// ─── Cache config ────────────────────────────────────────────────────────

const CACHE_TTL_DAYS = 30;
const CACHE_TTL_MS = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;

type CachedRisques = {
  report: RisquesReport;
  fetchedAt: Timestamp;
  inputsHash: string;
};

function inputsHashOf(lat: number, lng: number): string {
  return `${lat.toFixed(4)}|${lng.toFixed(4)}`;
}

// ─── Raw CF call (public — fallback cache miss) ──────────────────────────

export async function fetchRisques(
  lat: number,
  lng: number,
): Promise<RisquesReport> {
  const res = await callGetRisques({ lat, lng });
  return res.data;
}

// ─── Cached fetcher (UI + PDF) ───────────────────────────────────────────

export async function fetchRisquesCached(
  ficheId: string,
  lat: number,
  lng: number,
  forceRefresh = false,
): Promise<RisquesReport> {
  const user = auth.currentUser;
  if (!user) {
    return fetchRisques(lat, lng);
  }
  const uid = user.uid;
  const ref = doc(db, "users", uid, "fiches", ficheId, "risques", "latest");
  const wantedHash = inputsHashOf(lat, lng);

  // 1. Read cache
  let cached: CachedRisques | null = null;
  if (!forceRefresh) {
    try {
      const snap = await getDoc(ref);
      if (snap.exists()) {
        cached = snap.data() as CachedRisques;
        const age = Date.now() - cached.fetchedAt.toMillis();
        if (cached.inputsHash === wantedHash && age < CACHE_TTL_MS) {
          return cached.report;
        }
      }
    } catch (e) {
      console.warn("[risques] cache read failed, falling through", e);
    }
  }

  // 2. Call CF
  try {
    const fresh = await fetchRisques(lat, lng);
    // 3. Write cache fire-and-forget
    const payload: CachedRisques = {
      report: fresh,
      fetchedAt: Timestamp.now(),
      inputsHash: wantedHash,
    };
    setDoc(ref, payload).catch((e) => {
      console.warn("[risques] cache write failed (non-blocking)", e);
    });
    return fresh;
  } catch (err) {
    // 4. Résilience : CF 503 + cache stale → on sert le stale.
    if (cached) {
      console.warn(
        "[risques] CF failed, serving stale cache",
        err instanceof Error ? err.message : err,
      );
      return cached.report;
    }
    throw err;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; report: RisquesReport }
  | { status: "error"; message: string };

export function useRisques(
  lat: number | null,
  lng: number | null,
  ficheId?: string,
): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (lat == null || lng == null) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    const fetcher = ficheId
      ? fetchRisquesCached(ficheId, lat, lng)
      : fetchRisques(lat, lng);
    fetcher
      .then((report) => {
        if (cancelled) return;
        setState({ status: "ready", report });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setState({ status: "error", message: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [lat, lng, ficheId]);

  return state;
}
